import type { LucideIcon } from 'lucide-react'
import {
  Backpack,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  CircleHelp,
  Coins,
  Crown,
  Flag,
  Flame,
  Home,
  Medal,
  Package,
  PawPrint,
  Settings,
  Shield,
  Swords,
  Target,
  Trophy,
  User,
  UserPlus,
  Users,
  WandSparkles,
} from 'lucide-react'

export type NavigationBadgeKey = 'missions' | 'arena' | 'emporium' | 'friends'

export interface NavigationItem {
  href: string
  label: string
  title: string
  icon: LucideIcon
  badgeKey?: NavigationBadgeKey
  activePrefixes?: string[]
}

export interface NavigationGroup {
  id: string
  label: string
  items: NavigationItem[]
}

const PRIMARY_ITEMS: NavigationItem[] = [
  { href: '/dashboard', label: 'Home', title: 'Home', icon: Home },
  { href: '/missions', label: 'Missoes', title: 'Missoes', icon: Flag, badgeKey: 'missions' },
  { href: '/achievements', label: 'Conquistas', title: 'Conquistas', icon: Trophy },
  { href: '/manage/habits', label: 'Gestao', title: 'Habitos', icon: WandSparkles, activePrefixes: ['/manage'] },
  { href: '/emporium/shop', label: 'Loja', title: 'Loja', icon: Coins, badgeKey: 'emporium', activePrefixes: ['/emporium'] },
  { href: '/arena/leagues', label: 'Arena', title: 'Ligas', icon: Swords, badgeKey: 'arena', activePrefixes: ['/arena'] },
]

const MOBILE_PRIMARY_ITEMS: NavigationItem[] = [PRIMARY_ITEMS[0], PRIMARY_ITEMS[1], PRIMARY_ITEMS[4], PRIMARY_ITEMS[5]]

const DETAILED_ITEMS: NavigationItem[] = [
  {
    href: '/manage/habits',
    label: 'Habitos',
    title: 'Habitos',
    icon: CheckCircle2,
    activePrefixes: ['/manage/habits'],
  },
  {
    href: '/manage/goals',
    label: 'Metas',
    title: 'Metas',
    icon: Target,
    activePrefixes: ['/manage/goals'],
  },
  {
    href: '/manage/diary',
    label: 'Diario',
    title: 'Diario',
    icon: CalendarDays,
    activePrefixes: ['/manage/diary', '/diary'],
  },
  {
    href: '/manage/projects-and-tasks',
    label: 'Projetos e Tarefas',
    title: 'Projetos e Tarefas',
    icon: Package,
    activePrefixes: ['/manage/projects-and-tasks'],
  },
  {
    href: '/analytics',
    label: 'Analises',
    title: 'Analises',
    icon: BarChart3,
    activePrefixes: ['/analytics'],
  },

  { href: '/emporium/shop', label: 'Loja', title: 'Loja', icon: Coins, badgeKey: 'emporium', activePrefixes: ['/emporium/shop'] },
  { href: '/emporium/inventory', label: 'Inventario', title: 'Inventario', icon: Backpack },
  { href: '/emporium/pass', label: 'Habit Pass', title: 'Habit Pass', icon: Crown },

  { href: '/arena/leagues', label: 'Ligas', title: 'Ligas', icon: Medal },
  { href: '/arena/challenges', label: 'Desafios', title: 'Desafios', icon: Shield, activePrefixes: ['/arena/challenges', '/arena/challenge'] },
  { href: '/arena/duels', label: 'Duelos', title: 'Duelos', icon: Swords, activePrefixes: ['/arena/duels'] },
  { href: '/arena/boss', label: 'World Boss', title: 'World Boss', icon: Flame, activePrefixes: ['/arena/boss', '/boss'] },
  { href: '/arena/pet', label: 'Pet', title: 'Pet', icon: PawPrint, activePrefixes: ['/arena/pet', '/pet'] },
]

export function getPrimaryNavigationItems(): NavigationItem[] {
  return PRIMARY_ITEMS
}

export function getMobilePrimaryNavigationItems(): NavigationItem[] {
  return MOBILE_PRIMARY_ITEMS
}

export function getMoreNavigationItems(): NavigationItem[] {
  return DETAILED_ITEMS
}

export function getDesktopNavigationGroups(): NavigationGroup[] {
  return [
    {
      id: 'principal',
      label: 'Principal',
      items: [PRIMARY_ITEMS[0], PRIMARY_ITEMS[1], PRIMARY_ITEMS[2]],
    },
    {
      id: 'gestao',
      label: 'Gestao',
      items: DETAILED_ITEMS.slice(0, 5),
    },
    {
      id: 'loja',
      label: 'Loja',
      items: DETAILED_ITEMS.slice(5, 8),
    },
    {
      id: 'arena',
      label: 'Arena',
      items: DETAILED_ITEMS.slice(8, 13),
    },
    {
      id: 'conta',
      label: 'Conta',
      items: [
        { href: '/friends', label: 'Amigos', title: 'Amigos', icon: Users, badgeKey: 'friends' },
        { href: '/emporium/profile', label: 'Perfil', title: 'Perfil', icon: User, activePrefixes: ['/emporium/profile', '/emporium/arsenal', '/emporium/usercard'] },
      ],
    },
  ]
}

export function getProfileMenuItems(_username?: string): NavigationItem[] {
  return [
    { href: '/friends', label: 'Amigos', title: 'Amigos', icon: Users, badgeKey: 'friends' },
    { href: '/emporium/profile', label: 'Perfil', title: 'Perfil', icon: User },
    { href: '/settings', label: 'Configuracoes', title: 'Configuracoes', icon: Settings },
    { href: '/invites', label: 'Convites', title: 'Convites', icon: UserPlus },
    { href: '/support', label: 'Suporte', title: 'Suporte', icon: CircleHelp },
    { href: '/legal/terms', label: 'Legal', title: 'Legal', icon: Shield },
  ]
}

export function getSecondaryNavigationItems(): NavigationItem[] {
  return getProfileMenuItems()
}

export function isRouteActive(pathname: string, href: string): boolean {
  if (href === '/dashboard' || href === '/') {
    return pathname === '/dashboard' || pathname === '/'
  }

  return pathname === href || pathname.startsWith(`${href}/`)
}

export function isNavigationItemActive(pathname: string, item: NavigationItem): boolean {
  if (item.activePrefixes && item.activePrefixes.length > 0) {
    return item.activePrefixes.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
  }

  return isRouteActive(pathname, item.href)
}

export function getCurrentNavigationItem(pathname: string): NavigationItem {
  const allItems = [...DETAILED_ITEMS, ...PRIMARY_ITEMS]
  return allItems.find((item) => isNavigationItemActive(pathname, item)) ?? PRIMARY_ITEMS[0]
}
